/**
 * MatchCard Component (Compact Mobile-Optimized)
 * 
 * CRITICAL: This component reads live data from the centralized useLiveMatchStore.
 * All live UI must read from this single source of truth.
 * Never independently fetch or manage live state per component.
 */

import { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Match } from '@/lib/types';
import { cn } from '@/lib/utils';
import { createMatchSlug } from '@/lib/slugUtils';
import { ChevronRight } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { validateScore, validateLogoUrl, validateTeamName, validateMatchMinute } from '@/lib/dataValidation';
import { TeamLogo } from './TeamLogo';
import { useLiveMinute, useLiveScore, useLiveMatchStore } from '@/hooks/useLiveMatchStore';

// Compact team row component - optimized for mobile density
interface TeamRowProps {
  teamName: string; 
  logoUrl: string | null;
  score?: number | null;
  isWinning?: boolean;
}

const TeamRow = memo(function TeamRow({ 
  teamName, 
  logoUrl,
  score,
  isWinning = false
}: TeamRowProps) {
  return (
    <div className="flex items-center justify-between gap-2 h-6 sm:h-7">
      <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
        <TeamLogo logoUrl={logoUrl} teamName={teamName} size="2xs" className="shrink-0" />
        <span className={cn(
          "text-xs sm:text-sm leading-tight truncate match-text",
          isWinning ? "font-semibold" : "font-medium"
        )}>
          {teamName}
        </span>
      </div>
      {score !== undefined && score !== null && (
        <span className={cn(
          "text-xs sm:text-sm tabular-nums score-cell",
          isWinning ? "font-bold" : "font-semibold"
        )}>
          {score}
        </span>
      )}
    </div>
  );
});

// Compact live badge - stable, no re-renders causing animation restart
const LiveBadge = memo(function LiveBadge({ minute }: { minute?: string | null }) {
  return (
    <div className="flex items-center gap-1 text-destructive">
      <span className="relative flex h-1.5 w-1.5 sm:h-2 sm:w-2">
        <span className="animate-live-dot absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 sm:h-2 sm:w-2 bg-destructive"></span>
      </span>
      <span className="text-[10px] sm:text-xs font-bold tabular-nums">
        {minute || 'LIVE'}
      </span>
    </div>
  );
});

// Time/Status display - stable width to prevent layout shifts
const MatchStatus = memo(function MatchStatus({ 
  isLive, 
  isFinished, 
  liveMinute, 
  kickoffTime 
}: { 
  isLive: boolean; 
  isFinished: boolean; 
  liveMinute: string | null;
  kickoffTime: Date;
}) {
  if (isLive) {
    return <LiveBadge minute={liveMinute} />;
  }
  
  if (isFinished) {
    return <span className="text-[10px] sm:text-xs font-semibold text-muted-foreground">FT</span>;
  }
  
  return (
    <span className="text-[10px] sm:text-xs font-medium text-muted-foreground tabular-nums">
      {format(kickoffTime, 'HH:mm')}
    </span>
  );
});

interface MatchCardProps {
  match: Match;
}

function MatchCardComponent({ match }: MatchCardProps) {
  // Use centralized live data store for consistent live data everywhere
  const { isMatchLive } = useLiveMatchStore();
  const liveMinuteData = useLiveMinute(match.id);
  const liveScoreData = useLiveScore(match.id);
  
  // Determine if match is live from centralized store (single source of truth)
  const isLive = isMatchLive(match.id) || match.status === 'live';
  const isFinished = match.status === 'finished';

  // Use live scores from store if available, otherwise use match prop
  const homeScore = liveScoreData.isLive ? liveScoreData.homeScore : match.home_score;
  const awayScore = liveScoreData.isLive ? liveScoreData.awayScore : match.away_score;
  
  // Memoize expensive calculations
  const kickoffTime = useMemo(() => new Date(match.kickoff_time), [match.kickoff_time]);

  // Memoize validated values - use live scores from store
  const validatedData = useMemo(() => ({
    homeTeamName: validateTeamName(match.home_team) || 'Home',
    awayTeamName: validateTeamName(match.away_team) || 'Away',
    homeTeamLogo: validateLogoUrl(match.home_team_logo),
    awayTeamLogo: validateLogoUrl(match.away_team_logo),
    homeScore: validateScore(homeScore),
    awayScore: validateScore(awayScore),
  }), [match.home_team, match.away_team, match.home_team_logo, match.away_team_logo, homeScore, awayScore]);

  // Use live minute from centralized store
  const liveMinute = useMemo(() => {
    if (!isLive) return null;
    
    // CRITICAL: Use live minute from centralized store for consistency
    if (liveMinuteData.isLive && liveMinuteData.minute) {
      return `${liveMinuteData.minute}'`;
    }
    
    // Fallback to match prop data
    const currentMinute = match.statistics?.current_minute ?? match.statistics?.live_stats?.minute;
    if (currentMinute !== undefined && currentMinute > 0) {
      const validated = validateMatchMinute(currentMinute);
      return validated?.displayMinute || null;
    }
    
    return null;
  }, [isLive, liveMinuteData, match.statistics?.current_minute, match.statistics?.live_stats?.minute]);

  // Memoize slug
  const matchSlug = useMemo(() => 
    createMatchSlug(match.home_team, match.away_team, match.kickoff_time),
    [match.home_team, match.away_team, match.kickoff_time]
  );

  // Determine if either team is winning
  const homeWinning = (isLive || isFinished) && validatedData.homeScore !== null && validatedData.awayScore !== null && validatedData.homeScore > validatedData.awayScore;
  const awayWinning = (isLive || isFinished) && validatedData.homeScore !== null && validatedData.awayScore !== null && validatedData.awayScore > validatedData.homeScore;

  return (
    <Link
      to={`/match/${matchSlug}`}
      className={cn(
        // Mobile-optimized compact card: ~70-90px height target
        'block px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-lg bg-card border border-border/60',
        'transition-all duration-150 ease-out',
        'hover:bg-muted/50 hover:border-border',
        'active:bg-muted/70 active:scale-[0.995]',
        'group',
        isLive && 'border-l-2 border-l-destructive bg-destructive/[0.03]'
      )}
      aria-label={`View prediction for ${validatedData.homeTeamName} vs ${validatedData.awayTeamName}`}
    >
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Time/Status Column - fixed width for layout stability */}
        <div className="w-10 sm:w-12 shrink-0 flex justify-center">
          <MatchStatus 
            isLive={isLive}
            isFinished={isFinished}
            liveMinute={liveMinute}
            kickoffTime={kickoffTime}
          />
        </div>
        
        {/* Teams Column - optimized spacing */}
        <div className="flex-1 min-w-0 space-y-0.5 sm:space-y-1">
          <TeamRow 
            teamName={validatedData.homeTeamName} 
            logoUrl={validatedData.homeTeamLogo}
            score={(isLive || isFinished) ? validatedData.homeScore : undefined}
            isWinning={homeWinning}
          />
          <TeamRow 
            teamName={validatedData.awayTeamName} 
            logoUrl={validatedData.awayTeamLogo}
            score={(isLive || isFinished) ? validatedData.awayScore : undefined}
            isWinning={awayWinning}
          />
        </div>
        
        {/* Arrow - smaller on mobile */}
        <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground/40 group-hover:text-muted-foreground group-hover:translate-x-0.5 transition-all duration-150 shrink-0" />
      </div>
    </Link>
  );
}

// Custom comparison function for memo - only re-render when relevant data changes
export const MatchCard = memo(MatchCardComponent, (prevProps, nextProps) => {
  const prev = prevProps.match;
  const next = nextProps.match;
  
  return (
    prev.id === next.id &&
    prev.status === next.status &&
    prev.home_score === next.home_score &&
    prev.away_score === next.away_score &&
    prev.home_team === next.home_team &&
    prev.away_team === next.away_team &&
    prev.kickoff_time === next.kickoff_time &&
    // Include live minute in comparison for real-time updates
    prev.statistics?.current_minute === next.statistics?.current_minute &&
    prev.statistics?.half_status === next.statistics?.half_status
  );
});
